import { GenericResponse, ResponseError } from 'services/api/base';
import { User } from './users';

export const NA_VALUE = 0;

export enum SpecialModels {
  Climatology = 'climatologia',
  HistoricalRain = 'merge',
  Zero = 'zero',
}

export enum LockedRMVModels {
  Climatology = 'climatologia',
  HistoricalRain = 'merge',
  Zero = 'zero',
  ECMWF46_PROB = 'ecmwf46-prob',
  EC46_ONS_CLUSTER = 'ec46-ons-cluster',
}

export interface ListPersonalizedParams {
  ampereCases: boolean,
  page: number,
  pageSize: number,
}

export interface PersonalizedBlock {
  id?: number,
  id_cenario?: number,
  uuid?: string,
  ds_modelo: string,
  dt_data_prev: number,
  ck_rmv: boolean,
  dt_inicio: number,
  dt_fim: number,
  dt_recorte_inicio: number,
  dt_recorte_fim: number,
  preview?: string | null,
  membro?: string | null,
  run_time?: string | null,
}

export interface PersonalizedScenario {
  id?: number,
  id_estudo?: number,
  uuid?: string,
  ds_nome: string,
  blocos: PersonalizedBlock[],
}

export interface PersonalizedStudyParams {
  ready: boolean,
  vl_execution_counter: number,
  ds_nome_estudo: string,
  dt_inicio: number,
  dt_fim: number,
  ds_nome_cenario?: string,
  tipo_modelo_base?: string,
  ck_prev_rmv?: boolean,
  dt_data_prev?: number,
  dt_data_fim_hist?: string,
  dt_cenario_inicio?: number,
  dt_cenario_fim?: number,
  cenarios: PersonalizedScenario[],
  /** Fatores de ponderação para vazão média ponderada (VMEDPONDERADA); CSV string. */
  pesos_vmed_ponderada?: string,
  /** Produzir vazão média ponderada usando os fatores informados. */
  flag_produzir_vmedponderada?: boolean,
  /** Produzir somente VMED, VMEDPONDERADA, PERCENTIL e CLUSTER (economia de tempo). */
  flag_produzir_somente_cenarios_probabilisticos?: boolean,
  /** Produzir arquivo CSV com ENAs de todos os cenários (agregação por REE e SUB). */
  flag_compilar_resumo_multicenarios?: boolean,
  /** Versão do SMAP: true = SmapR V1.4.1 (nova), false/ausente = V1.2.0 (antiga). */
  ck_is_nt?: boolean,
}

export interface PersonalizedStudyResponse {
  id: number,
  id_empresa: number,
  id_user: number,
  ds_nome_estudo: string,
  dt_inicio: number,
  dt_fim: number,
  dt_last_execution: number | null,
  dt_last_response: number | null,
  vl_execution_counter: number,
  ready: boolean,
  uuid: string,
  ck_internal_error: boolean,
  ck_is_file: boolean,
  ck_is_nt: boolean,
  cenarios: PersonalizedScenario[],
  user: User,
}

export type SaveCustomStudyResponse = GenericResponse<PersonalizedStudyResponse>;

export interface PersonalizedDownloadLinkParams {
  caso_id: number,
}

export interface DownloadLinkData {
  link: string,
}

export type DownloadLinkResponse = GenericResponse<DownloadLinkData>;

export interface PersonalizedBlockDraft {
  key: string,
  model: string,
  runtime: number,
  member: string,
  rmv: number,
  forecastDate: Date,
  startDate: Date,
  endDate: Date,
  sliceStartDate: Date,
  sliceEndDate: Date,
}

export interface PersonalizedScenarioDraft {
  name: string,
  blocks: PersonalizedBlockDraft[],
}

export interface RawPersonalizedHorizonResponse {
  status: number,
  data: {
    method: string,
    response: string,
    status_code: number,
    data_inicial: number,
    data_final: number,
    num_dias: number,
    message?: string,
  },
}

export interface PersonalizedHorizonParams {
  forecastDate: number,
  model: string,
  runtime: number,
  member: string,
}

export interface PersonalizedHorizon {
  minDate: number,
  maxDate: number,
  days: number,
}

export const transformPersonalizedMaxHorizonResponse = (
  response: RawPersonalizedHorizonResponse | undefined,
): {
  data: PersonalizedHorizon | undefined,
  error: ResponseError | undefined,
} => ({
  data: response?.data.status_code === 200 ? {
    minDate: response.data.data_inicial,
    maxDate: response.data.data_final,
    days: response.data.num_dias,
  } : undefined,
  error: (response?.data.status_code && response?.data.status_code !== 200) ? {
    status: response?.data.status_code,
    data: `${response?.data.response}: ${response?.data.message}`,
  } : undefined,
});

/** Resultado do Reenviar: `success` é o aceite do serviço Flux (false = o Flux recusou). */
export interface ResendStudyResult {
  success: boolean,
}

/** Corpo do `produtos/flux/re-execute`: `{status, success}` SEM `code`; o erro vem no envelope. */
interface RawResendStudyResponse {
  success?: boolean,
  code?: number,
  message?: string,
  description?: string,
}

/**
 * Lê a resposta do Reenviar sem o baseQuery normalizado, que trataria como erro todo corpo sem
 * `code: 200`: o sucesso do re-execute não traz `code` (wsgi-beta `controller/flux.py:245`).
 * - Erro de transporte vira `ResponseError`, como no baseQuery.
 * - Corpo vazio vira `ResponseError` (`CUSTOM_ERROR`).
 * - Envelope com `code` diferente de 200 vira `ResponseError` com `message` e `description`.
 * - Qualquer outro corpo vira `{ success }`, verdadeiro só com `success: true`.
 */
export const toResendStudyResult = (
  result: { data?: unknown, error?: unknown },
): { data: ResendStudyResult } | { error: ResponseError } => {
  if (result.error) {
    const transportError = result.error as { status: number | string, error?: string };
    return { error: { status: transportError.status, data: transportError.error ?? '' } };
  }
  // Corpo vazio não é "o Flux recusou": é resposta que não deu para ler
  if (!result.data || typeof result.data !== 'object') {
    return { error: { status: 'CUSTOM_ERROR', data: 'Resposta vazia do re-execute' } };
  }
  const body = result.data as RawResendStudyResponse;
  if (typeof body.code === 'number' && body.code !== 200) {
    return {
      error: { status: body.code, data: body.message ?? '', description: body.description },
    };
  }
  return { data: { success: body.success === true } };
};
